/**
 * FuturesMarginReportPage — `/futures/margin-report`.
 *
 * 1:1 với Access `Report.rpFuturesMargin`:
 *   - Top header chia 2 zone: Hedging Config (trái) + BẢNG QUY ĐỔI (phải).
 *   - Hàng cấu hình thứ 2: Ký quỹ/lot Robusta · Arabica · USD/VND rate · Ngày.
 *     Nhãn "Dự phòng giá biến động" (cột 11–15: Market + tiền, UPL, Margin Excess, %, No.lots) + "Tỷ lệ ký quỹ cắt lỗ" (16–17)
 *     hàng đầu khối bank — khớp cột Market +$, UPL, Margin Excess, %, No.lots và 2 cột tỷ lệ (`report_rpFuturesMargin.txt`).
 *   - 3 bank (VietinBank → BIDV → MBBank) trong **một** `<Table>` (3 `<TableBody>`) để cột thẳng hàng một khối.
 *     Mỗi bank: banner + header + month rows + Total; rowSpan cột 7–17 như Access.
 *   - Màu chữ / nền động: `report_rpFuturesMargin.txt` `Private Sub color_page()` + `ForeColor` design-time
 *     (OLE 13369344→#CC6600, 2366701→#24190D cho Avg Price); heat OTE/UPL2 = RGB(248,255*r,255*r).
 *   - Grand Total row cuối cộng 3 bank.
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'

import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useGetFuturesBankStatementQuery,
  useGetFuturesReportQuery,
  useGetMarginReportBankDetailQuery,
  useGetMarginReportChangeValuesQuery,
  usePutMarginReportChangeValuesMutation,
} from '../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
} from './futuresAccessUi'

type ReportRow = Record<string, unknown>

const BANKS = [
  { key: 'Vietinbank', label: 'VietinBank' },
  { key: 'BIDV', label: 'BIDV' },
  { key: 'MBbank', label: 'MBBank' },
] as const

// Số dòng month/bank được render = số row backend trả về (không cap, không pad).
// Khi backend trả 0 dòng → render 1 placeholder để bank section vẫn hiển thị.

// ─── Master 17-col grid: align với Access `rpFuturesMargin` 1:1 ────────────
// Cột 1–6: per-month data (Month/Positions/Avg Price/Initial Margin/OTE-UPL/OTE-UPL+X$)
// Cột 7–10: current-state aggregate (Balance/Margin Excess/% Margin Excess/No. lots + "Ngày")
// Cột 11: scenario marker "Market +X$" (txtLblVTB ở Access — vertical text, rowspan toàn body)
// Cột 12–15: scenario aggregate (UPL/Margin Excess/% Margin Excess/No. lots)
// Cột 16–17: stoploss (Tỷ lệ Ký quỹ / Tỷ lệ Ký quỹ +X$)
// Web: bảng `table-layout: auto` + lưới header theo min-content — đủ chỗ text; scroll ngang/dọc một lần ở `reportShellSx`.
/** 17 cột Access — độ rộng theo nội dung (không % + fixed layout → tránh cắt chữ màn hẹp). */
const MARGIN_REPORT_GRID_COLUMNS = 'repeat(17, minmax(min-content, max-content))'

/** Lưới 17 cột đồng bộ với bảng: tối thiểu full khung, nở theo nội dung. */
const marginReportGridSx = {
  display: 'grid',
  gridTemplateColumns: MARGIN_REPORT_GRID_COLUMNS,
  columnGap: 0,
  width: 'max-content',
  minWidth: '100%',
}

// ─── Shared sx ──────────────────────────────────────────────────────────────

const commandBarSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 0.75,
  flexWrap: 'wrap' as const,
  px: 1,
  py: 0.5,
  mb: 0.75,
  borderRadius: 2,
  border: '1px solid var(--app-border-subtle, rgba(11, 54, 51, 0.12))',
  borderBottom: '2px solid rgba(244, 223, 156, 0.55)',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffbf7 100%)',
  boxShadow: '0 1px 2px rgba(11, 54, 51, 0.04)',
}

const commandBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.15,
  minHeight: 22,
  fontSize: '0.66rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
  color: 'primary.main',
  borderColor: 'primary.main',
}

const reportShellSx = {
  flex: 1,
  minHeight: 0,
  /** Một lớp scroll duy nhất cho toàn báo cáo (ngang + dọc). */
  overflow: 'auto',
  border: '1px solid var(--app-border-subtle, rgba(11, 54, 51, 0.12))',
  borderRadius: '14px',
  bgcolor: 'var(--app-card-bg, #ffffff)',
  p: 1,
  boxShadow: 'var(--app-shadow-soft, 0 10px 30px -26px rgba(11, 54, 51, 0.42))',
}

const reportPageSx = {
  width: 'max(100%, 1180px)',
  minWidth: 0,
  minHeight: 0,
  overflow: 'visible',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffdf8 100%)',
  border: '1px solid rgba(11, 54, 51, 0.06)',
  borderRadius: '12px',
  p: 1.25,
  fontSize: '0.68rem',
  color: '#000000',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.92)',
}

/** Tier 1 — tiêu đề sub-report (Hedging, Ngày…): đồng bộ Summary. */
const sectionTitleSx = {
  height: 20,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.66rem',
  fontWeight: 900,
  letterSpacing: '0.07em',
  color: '#000000',
  background: 'linear-gradient(180deg, #ffffff 0%, #fffbf4 64%, rgba(244, 223, 156, 0.36) 100%)',
  border: '1px solid rgba(218, 131, 60, 0.16)',
  borderBottom: '2px solid rgba(169, 94, 37, 0.22)',
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9)',
  textTransform: 'uppercase' as const,
}

/** Bảng quy đổi / Dự phòng — tier 1b copper stripe (đồng bộ PROPS Summary). */
const propTitleSx = {
  ...sectionTitleSx,
  color: '#000000',
  background: 'linear-gradient(180deg, #ffffff 0%, #fff4e8 76%, rgba(251, 212, 168, 0.5) 100%)',
  border: '1px solid rgba(218, 131, 60, 0.22)',
  borderBottom: '2px solid rgba(169, 94, 37, 0.28)',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9), inset 3px 0 0 rgba(218, 131, 60, 0.45)',
}

/** Tiêu đề khối Hedging / Ngày / Bảng quy đổi — chữ nhỏ hơn band section trong bảng. */
const topBannerSectionTitleSx = {
  ...sectionTitleSx,
  fontSize: '0.58rem',
  height: 18,
  letterSpacing: '0.05em',
}

const topBannerPropTitleSx = {
  ...propTitleSx,
  fontSize: '0.58rem',
  height: 18,
  letterSpacing: '0.05em',
}

/** Tier 2 — Tỷ lệ ký quỹ cắt lỗ (cùng họ TOTAL POSITIONS Summary, không teal). */
const darkTitleSx = {
  height: 22,
  px: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  color: '#000000',
  background: 'linear-gradient(180deg, #fff8e6 0%, #f4df9c 52%, #e4c36c 100%)',
  border: '1px solid rgba(169, 94, 37, 0.34)',
  borderBottom: '2px solid rgba(11, 54, 51, 0.24)',
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.72)',
  textTransform: 'uppercase' as const,
}

const marginTotalRowSx = {
  fontWeight: 800,
  color: '#000000',
  backgroundColor: 'rgba(244, 223, 156, 0.16)',
  borderTop: '2px solid rgba(169, 94, 37, 0.22)',
}

const marginGrandTotalRowSx = {
  fontWeight: 800,
  fontSize: '0.58rem',
  color: '#000000',
  background: 'linear-gradient(180deg, #fffefb 0%, #f9e8b8 100%)',
  borderTop: '3px double rgba(11, 54, 51, 0.32)',
}

const tableSx = {
  tableLayout: 'auto' as const,
  width: 'max-content',
  minWidth: '100%',
  borderCollapse: 'separate' as const,
  borderSpacing: 0,
  mt: 0,
  '& th, & td': {
    height: 20,
    px: 0.35,
    py: 0.1,
    border: '1px solid rgba(11, 54, 51, 0.07)',
    fontSize: '0.6rem',
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    color: '#000000',
    textAlign: 'center',
  },
  '& th': {
    fontWeight: 800,
    letterSpacing: '0.04em',
    color: '#000000',
    backgroundColor: '#fffdfb',
    backgroundImage: 'linear-gradient(180deg, #ffffff 0%, #fff9f4 55%, rgba(244, 223, 156, 0.36) 100%)',
    borderBottom: '1px solid rgba(169, 94, 37, 0.2)',
    minWidth: 'min-content',
    px: 0.4,
  },
  '& tbody tr td': {
    bgcolor: '#ffffff',
  },
  '& td.num': {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
    minWidth: 'min-content',
    px: 0.4,
  },
  '& .bank-banner': {
    fontSize: '0.78rem',
    fontWeight: 800,
    color: '#000000',
    background: 'linear-gradient(180deg, #f0dcc8 0%, #e2c4a4 42%, #cfad88 100%)',
    height: 24,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  },
  '& .scenario-banner': {
    fontSize: '0.74rem',
    fontWeight: 800,
    color: '#000000',
    background: 'linear-gradient(180deg, #fffefb 0%, #fff2e0 62%, rgba(240, 214, 180, 0.55) 100%)',
    height: 24,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  '& .stoploss-banner': {
    fontSize: '0.74rem',
    fontWeight: 800,
    color: '#000000',
    background: 'linear-gradient(180deg, #fad6d2 0%, #f0a8a0 48%, #e08880 100%)',
    height: 24,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
  },
  '& td.span-cell': {
    verticalAlign: 'middle',
    fontWeight: 700,
    bgcolor: '#fffbf6',
  },
  '& td.span-cell-scenario': {
    verticalAlign: 'middle',
    fontWeight: 700,
    bgcolor: 'rgba(244, 223, 156, 0.14)',
  },
  '& td.span-cell-stoploss': {
    verticalAlign: 'middle',
    fontWeight: 700,
    bgcolor: '#fff8f4',
  },
  '& tbody tr.total td': {
    ...marginTotalRowSx,
  },
  '& tbody tr.grand-total td': {
    ...marginGrandTotalRowSx,
  },
  /** Cột 1 Month: full_symbol dài (hàng header từng bank nằm trong tbody + `th`). */
  '& tbody tr.bank-subhead .MuiTableCell-root:first-of-type': {
    minWidth: 'max(5.5rem, min-content)',
  },
  '& tbody tr.month-row > .MuiTableCell-root:first-of-type': {
    minWidth: 'max(5.5rem, min-content)',
  },
  /** Hàng nhãn khối scenario/stoploss (chỉ VietinBank), ngay trên banner bank. */
  '& tbody tr.margin-bank-section-labels .MuiTableCell-root': {
    borderBottom: '1px solid rgba(11, 54, 51, 0.1)',
  },
  /** Khoảng cách giữa các bank trong cùng bảng. */
  '& tbody.bank-section-spacer .MuiTableCell-root': {
    border: 0,
    py: 0.5,
    height: 8,
    bgcolor: 'transparent',
    lineHeight: 0,
  },
}

/** Chiều cao hàng mặc định — khớp `tableSx` `& th, & td` height. */
const MARGIN_TABLE_ROW_HEIGHT_BASE = 20

/** Ước lượng chiều cao tối thiểu cho nhãn Market dọc (`writingMode: vertical-rl`). */
function estimateMarketVerticalLabelMinHeight(label: string): number {
  const fontPx = 0.56 * 16
  const charW = fontPx * 0.65
  return Math.ceil(label.length * charW + 6)
}

/** Chiều cao hàng dữ liệu (month); Total luôn `MARGIN_TABLE_ROW_HEIGHT_BASE`. */
function marginMonthRowHeight(monthRowCount: number, marketLabel: string): number {
  const totalH = MARGIN_TABLE_ROW_HEIGHT_BASE
  const labelMin = estimateMarketVerticalLabelMinHeight(marketLabel)
  const spanNatural = monthRowCount * totalH + totalH
  if (spanNatural >= labelMin || monthRowCount <= 0) return totalH
  return Math.ceil((labelMin - totalH) / monthRowCount)
}

const marginMonthRowSx = (height: number) => ({
  '& td': { height },
})

// ─── Formatters ─────────────────────────────────────────────────────────────

const displayMoney = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.abs(n))
  if (n === 0) return '$ -'
  return n < 0 ? `$ (${formatted})` : `$ ${formatted}`
}

const displayMoney2 = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Math.abs(n),
  )
  if (n === 0) return '$ -'
  return n < 0 ? `$ (${formatted})` : `$ ${formatted}`
}

const displayVnd = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Math.abs(n))
  if (n === 0) return '0 ₫'
  return n < 0 ? `(${formatted}) ₫` : `${formatted} ₫`
}

const displayPercent = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const fmt = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 2 })
  if (n < 0) return `(${fmt.format(Math.abs(n))})`
  return fmt.format(n)
}

const displayLots = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('en-US').format(Math.abs(n))
  if (n === 0) return '0 lots'
  return n < 0 ? `(${formatted}) lots` : `${formatted} lots`
}

/** Lots display that preserves string sentinels (e.g. "Âm ký quỹ" — view emits text
 * marker khi margin_excess âm). Number → "N lots"; string → as-is. */
const displayLotsOrText = (value: number | string | null): string => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string') return value
  const formatted = new Intl.NumberFormat('en-US').format(Math.abs(value))
  if (value === 0) return '0 lots'
  return value < 0 ? `(${formatted}) lots` : `${formatted} lots`
}

const displayPosition = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  if (n === 0) return '-'
  const formatted = new Intl.NumberFormat('en-US').format(Math.abs(n))
  return n < 0 ? `(${formatted})` : formatted
}

const displayNumber = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Math.abs(n))
  if (n === 0) return '0'
  return n < 0 ? `(${formatted})` : formatted
}

const toRows = (items: unknown[] | undefined): ReportRow[] => (items ?? []) as ReportRow[]

const bankEquals = (row: ReportRow, bank: string) =>
  String(row.bank ?? '').trim().toLowerCase() === bank.toLowerCase()

const pickNumber = (row: ReportRow | undefined, ...keys: string[]): number | null => {
  if (!row) return null
  for (const key of keys) {
    const value = row[key]
    if (value === null || value === undefined || value === '') continue
    const n = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(n)) return n
  }
  return null
}

/** Returns numeric value when parseable; otherwise returns the raw non-empty string
 * (e.g. sentinel "Âm ký quỹ" trong `no_lots_1`/`no_lots_2`). */
const pickNumberOrText = (row: ReportRow | undefined, ...keys: string[]): number | string | null => {
  if (!row) return null
  for (const key of keys) {
    const value = row[key]
    if (value === null || value === undefined || value === '') continue
    const n = typeof value === 'number' ? value : Number(value)
    if (Number.isFinite(n)) return n
    if (typeof value === 'string') {
      const s = value.trim()
      if (s) return s
    }
  }
  return null
}

const parseConfigNumber = (v: string | undefined): number => {
  if (!v) return 0
  const n = Number(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const sumOrNull = (values: Array<number | null>): number | null => {
  const finite = values.filter((v): v is number => v != null && Number.isFinite(v))
  if (finite.length === 0) return null
  return finite.reduce((a, b) => a + b, 0)
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

const todayStr = () => {
  const d = new Date()
  const dd = String(d.getDate()).padStart(2, '0')
  const mmm = MONTH_SHORT[d.getMonth()]
  return `${dd}-${mmm}-${d.getFullYear()}`
}

// ─── Conditional formatting (mirror Access VBA `color_page`) ────────────────
// Design-time `ForeColor` (OLE long → #RRGGBB): `frontend/plan/access_all/report_rpFuturesMargin.txt`
// (vd. txtVTBBalance / txtVTB61 `ForeColor =13369344`, txtVTB33 avg_price `ForeColor =2366701`).
// Runtime `color_page`: heat BackColor RGB(248,255*r,255*r); traffic % / tỷ lệ BackColor+ForeColor như VBA.

/** Access OLE `ForeColor = 13369344` → #CC6600 — hầu hết số tiền, lots, OTE/UPL, positions… (override #000000 per user 2026-05-21). */
const MARGIN_DESIGN_FORE_ORANGE = '#000000'
/** Access OLE `ForeColor = 2366701` → #24190D — cột Avg Price (per-month). (override #000000 per user 2026-05-21) */
const MARGIN_DESIGN_FORE_AVG_PRICE = '#000000'

const marginOrangeNumSx = { color: MARGIN_DESIGN_FORE_ORANGE, fontWeight: 700 as const }
const marginAvgPriceNumSx = { color: MARGIN_DESIGN_FORE_AVG_PRICE, fontWeight: 700 as const }

/** Heat map cho OTE/UPL Market±X$ (per-bank min/max → ratio → rgb(248, 255*r, 255*r)). */
const heatMapBgColor = (value: number | null, min: number, max: number): string | undefined => {
  if (value == null || !Number.isFinite(value)) return undefined
  const range = max - min
  if (range <= 0) return undefined
  const ratio = Math.max(0, Math.min(1, (value - min) / range))
  const ch = Math.round(255 * ratio)
  return `rgb(248, ${ch}, ${ch})`
}

/** 3-tier traffic light cho % Margin Excess & Tỷ lệ ký quỹ.
 * - <0.1: đỏ + chữ trắng | 0.1–0.4: vàng + chữ đen | >0.4: xanh lá + chữ trắng
 */
const trafficLight = (value: number | null): { bg: string; fg: string } | undefined => {
  if (value == null || !Number.isFinite(value)) return undefined
  if (value < 0.1) return { bg: 'rgb(255, 0, 0)', fg: '#FFFFFF' }
  if (value <= 0.4) return { bg: 'rgb(255, 255, 0)', fg: '#000000' }
  return { bg: 'rgb(80, 170, 76)', fg: '#FFFFFF' }
}

/** Banner scenario zone (txtLblVTB/BIDV/MB): green nếu price_change>=0, đỏ nếu <0. */
const scenarioBannerColor = (priceChange: number): { bg: string; fg: string } =>
  priceChange >= 0
    ? { bg: 'rgb(80, 170, 76)', fg: '#FFFFFF' }
    : { bg: 'rgb(255, 0, 0)', fg: '#FFFFFF' }

// ─── Change Input Value dialog ───────────────────────────────────────────────

interface ChangeInputDialogProps {
  open: boolean
  initialValues: Record<string, string>
  saving: boolean
  onClose: () => void
  onSave: (values: Record<string, string>) => void
}

const CHANGE_FIELDS: { name: string; label: string; format?: 'usd' | 'vnd' | 'int' }[] = [
  { name: 'lot_hedge', label: 'Lots to hedge', format: 'int' },
  { name: 'price_change', label: 'Price change', format: 'int' },
  { name: 'change_vnd', label: 'Conversion (VND)', format: 'vnd' },
  { name: 'change_usd', label: 'Conversion (USD)', format: 'usd' },
  { name: 'kyquy_robusta', label: 'Margin/Lot - Robusta', format: 'usd' },
  { name: 'kyquy_arabica', label: 'Margin/Lot - Arabica', format: 'usd' },
  { name: 'balance_vietinbank', label: 'Balance - VietinBank', format: 'usd' },
  { name: 'balance_bidv', label: 'Balance - BIDV', format: 'usd' },
  { name: 'balance_mbbank', label: 'Balance - MBBank', format: 'usd' },
]

function ChangeInputValueDialog({ open, initialValues, saving, onClose, onSave }: ChangeInputDialogProps) {
  const [state, setState] = useState<Record<string, string>>({})
  useEffect(() => {
    if (open) setState({ ...initialValues })
  }, [open, initialValues])

  const update = (name: string, value: string) => setState((prev) => ({ ...prev, [name]: value }))

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={futuresFormDialogTitleSx}>
        <Typography component="span" sx={{ flex: '1 1 auto', fontSize: '0.85rem', fontWeight: 700, minWidth: 0 }}>
          Futures Control - Margin Report — Change Input Value
        </Typography>
        <Box sx={futuresFormDialogActionsBoxSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => onSave(state)}
            disabled={saving}
            sx={futuresFormDialogActionBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save All'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={futuresFormDialogActionBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers>
        <Stack spacing={1.25} sx={{ mt: 0.5 }}>
          {CHANGE_FIELDS.map((field) => (
            <TextField
              key={field.name}
              label={field.label}
              size="small"
              fullWidth
              type="number"
              inputProps={{ step: field.format === 'int' ? 1 : 'any' }}
              value={state[field.name] ?? ''}
              onChange={(e) => update(field.name, e.target.value)}
            />
          ))}
          <Stack direction="row" spacing={1}>
            <TextField
              label="Position month"
              size="small"
              fullWidth
              value={state['position_month'] ?? ''}
              onChange={(e) => update('position_month', e.target.value)}
            />
            <TextField
              label="Bank"
              size="small"
              fullWidth
              value={state['bank'] ?? ''}
              onChange={(e) => update('bank', e.target.value)}
            />
            <TextField
              label="Price"
              size="small"
              fullWidth
              type="number"
              inputProps={{ step: 'any' }}
              value={state['position_month_price'] ?? ''}
              onChange={(e) => update('position_month_price', e.target.value)}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

// ─── Per-bank totals (computed client-side, mirror Access DSum) ─────────────

interface BankTotals {
  positions: number | null
  initialMargin: number | null
  oteUpl: number | null
  oteUpl2: number | null
}

interface BankAggregate {
  balance: number | null
  marginExcess1: number | null
  perMarginExcess1: number | null
  noLots1: number | string | null
  upl: number | null
  marginExcess2: number | null
  perMarginExcess2: number | null
  noLots2: number | string | null
  tyLeKyQuy: number | null
  tyLeKyQuy2: number | null
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function FuturesMarginReportPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const margin = useGetFuturesReportQuery({ kind: 'margin-report', page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const bankDetail = useGetMarginReportBankDetailQuery({ page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE })
  const changeValuesQ = useGetMarginReportChangeValuesQuery()
  const vtbStatement = useGetFuturesBankStatementQuery({ bank: 'Vietinbank', page: 1, pageSize: 1 })
  const [putChangeValues, putState] = usePutMarginReportChangeValuesMutation()

  const [changeOpen, setChangeOpen] = useState(false)

  const loading =
    margin.isLoading || bankDetail.isLoading || changeValuesQ.isLoading || vtbStatement.isLoading
  const fetching =
    margin.isFetching || bankDetail.isFetching || changeValuesQ.isFetching || vtbStatement.isFetching
  const hasError = Boolean(margin.error || bankDetail.error || changeValuesQ.error)

  const summaryRows = toRows(margin.data?.items)
  const bankDetailRows = toRows(bankDetail.data?.items)
  const cfg = changeValuesQ.data ?? {}

  const lotHedge = parseConfigNumber(cfg['lot_hedge'])
  const priceChange = parseConfigNumber(cfg['price_change'])
  const kyquyRobusta = parseConfigNumber(cfg['kyquy_robusta'])
  const kyquyArabica = parseConfigNumber(cfg['kyquy_arabica'])
  const changeUsd = parseConfigNumber(cfg['change_usd'])
  const changeVnd = parseConfigNumber(cfg['change_vnd'])

  const usdVndRate = useMemo(() => {
    const rows = toRows(vtbStatement.data?.items)
    return pickNumber(rows[0], 'rateBuyCurrency') ?? 0
  }, [vtbStatement.data?.items])

  const handleSave = async (values: Record<string, string>) => {
    try {
      const month = (values['position_month'] ?? '').trim()
      const bank = (values['bank'] ?? '').trim()
      const positionPrice = (values['position_month_price'] ?? '').trim()
      const body: Record<string, string> = { ...values }
      delete body.position_month
      delete body.bank
      delete body.position_month_price
      if (month && bank && positionPrice) {
        body[`${month}_${bank}`] = positionPrice
      } else if ((month && !bank) || (!month && bank)) {
        snack.error('Please enter both Position month and Bank to update position.')
        return
      }
      await putChangeValues(body).unwrap()
      // Reload báo cáo + bank detail + change-values (PUT đã invalidate FuturesControl nhưng refetch tường minh để UI chắc chắn có số mới ngay).
      await Promise.all([
        margin.refetch(),
        bankDetail.refetch(),
        changeValuesQ.refetch(),
      ]).catch(() => undefined)
      snack.success('Margin Report configuration saved.')
      setChangeOpen(false)
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  // ── Helpers per bank ────────────────────────────────────────────────────
  const bankRows = (bank: string): ReportRow[] =>
    bankDetailRows
      .filter((r) => bankEquals(r, bank))
      .sort((a, b) => (pickNumber(a, 'rank') ?? 0) - (pickNumber(b, 'rank') ?? 0))

  const bankAgg = (bank: string): BankAggregate => {
    const row = summaryRows.find((r) => bankEquals(r, bank))
    return {
      balance: pickNumber(row, 'balance'),
      marginExcess1: pickNumber(row, 'marginExcess1', 'margin_excess_1'),
      perMarginExcess1: pickNumber(row, 'perMarginExcess1', 'per_margin_excess_1'),
      noLots1: pickNumberOrText(row, 'noLots1', 'no_lots_1'),
      upl: pickNumber(row, 'upl'),
      marginExcess2: pickNumber(row, 'marginExcess2', 'margin_excess_2'),
      perMarginExcess2: pickNumber(row, 'perMarginExcess2', 'per_margin_excess_2'),
      noLots2: pickNumberOrText(row, 'noLots2', 'no_lots_2'),
      tyLeKyQuy: pickNumber(row, 'tyLeKyQuy', 'ty_le_ky_quy'),
      tyLeKyQuy2: pickNumber(row, 'tyLeKyQuy2', 'ty_le_ky_quy_2'),
    }
  }

  const bankTotals = (rows: ReportRow[]): BankTotals => ({
    positions: sumOrNull(rows.map((r) => Math.abs(pickNumber(r, 'openPosition') ?? 0) || null)),
    initialMargin: sumOrNull(rows.map((r) => Math.abs(pickNumber(r, 'initialMargin') ?? 0) || null)),
    oteUpl: sumOrNull(rows.map((r) => pickNumber(r, 'oteUpl'))),
    oteUpl2: sumOrNull(rows.map((r) => pickNumber(r, 'oteUpl2'))),
  })

  // ── Header config: BẢNG QUY ĐỔI numbers ─────────────────────────────────
  const usdToVnd = changeUsd && usdVndRate ? changeUsd * usdVndRate : 0
  const vndToUsd = changeVnd && usdVndRate ? changeVnd / usdVndRate : 0
  const lotsNeed = lotHedge && kyquyRobusta ? lotHedge * kyquyRobusta : 0 // Ký quỹ khả dụng cần (USD)
  const lotsNeedVnd = lotsNeed && usdVndRate ? lotsNeed * usdVndRate : 0

  // Dynamic labels — mirror Access ControlSource trên `Label558` và `Label2303`:
  //   OTE/UPL: `IIf(price>0, "OTE/UPL (+X$)", "OTE/UPL (X$)")` (KHÔNG có "Market")
  //   Tỷ lệ Ký quỹ: `IIf(price>0, "Tỷ lệ Ký quỹ (+X$)", ...)` (giữ nguyên capital K)
  const priceTag = `${priceChange > 0 ? '+' : ''}${priceChange}$`
  const oteUpl2Label = priceChange ? `OTE/UPL (${priceTag})` : 'OTE/UPL'
  const tyLeKyQuy2Label = priceChange ? `Margin Ratio (${priceTag})` : 'Margin Ratio'
  // Vertical scenario banner — txtLblVTB ControlSource: `Market +X$` / `Market X$`.
  const marketLabel = `Market ${priceTag}`

  // ── Grand totals ─────────────────────────────────────────────────────────
  const allBankAggs = BANKS.map((b) => bankAgg(b.key))
  const allBankTotals = BANKS.map((b) => bankTotals(bankRows(b.key)))

  const grandTotalPositions = sumOrNull(allBankTotals.map((t) => t.positions))
  const grandTotalInitialMargin = sumOrNull(allBankTotals.map((t) => t.initialMargin))
  const grandTotalOteUpl = sumOrNull(allBankTotals.map((t) => t.oteUpl))
  const grandTotalOteUpl2 = sumOrNull(allBankTotals.map((t) => t.oteUpl2))
  const grandTotalBalance = sumOrNull(allBankAggs.map((a) => a.balance))
  const grandTotalMarginExcess1 = sumOrNull(allBankAggs.map((a) => a.marginExcess1))
  const grandTotalUpl = sumOrNull(allBankAggs.map((a) => a.upl))
  const grandTotalMarginExcess2 = sumOrNull(allBankAggs.map((a) => a.marginExcess2))
  const grandTotalNoLots1 =
    grandTotalMarginExcess1 != null && grandTotalMarginExcess1 > 0 && kyquyRobusta
      ? Math.round(grandTotalMarginExcess1 / kyquyRobusta)
      : null
  const grandTotalNoLots2 =
    grandTotalMarginExcess2 != null && grandTotalMarginExcess2 > 0 && kyquyRobusta
      ? Math.round(grandTotalMarginExcess2 / kyquyRobusta)
      : null

  // Fragment-returning helper: 2 grid items (label + value) so parent grid can
  // align label-width across rows (auto-sized columns).
  const renderConfigField = (label: string, value: string, compact?: boolean) => {
    const labelFs = compact ? '0.58rem' : '0.66rem'
    const valueFs = compact ? '0.62rem' : '0.7rem'
    return (
      <>
        <Typography
          component="span"
          sx={{
            fontSize: labelFs,
            fontWeight: 700,
            color: '#000000',
            whiteSpace: 'nowrap',
            alignSelf: 'center',
          }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            minWidth: 0,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            border: '1px solid rgba(11, 54, 51, 0.20)',
            background: '#FFFFFF',
            fontSize: valueFs,
            fontWeight: 700,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: '"tnum"',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value || '—'}
        </Box>
      </>
    )
  }

  const renderBankTableBody = (bank: (typeof BANKS)[number]) => {
    const rows = bankRows(bank.key)
    const agg = bankAgg(bank.key)
    const totals = bankTotals(rows)
    // Dynamic row count: dùng đúng số row trả về; nếu 0 thì 1 placeholder.
    const displayRows: (ReportRow | null)[] = rows.length === 0 ? [null] : rows
    // rowSpan cho các span cells = số month rows + 1 (Total row)
    const aggRowSpan = displayRows.length + 1
    const monthRowHeight = marginMonthRowHeight(displayRows.length, marketLabel)
    const monthRowSx = marginMonthRowSx(monthRowHeight)

    // Heat map range: per-bank min/max of ote_upl2 across actual rows.
    const ote2Values = rows
      .map((r) => pickNumber(r, 'oteUpl2'))
      .filter((v): v is number => v != null && Number.isFinite(v))
    const ote2Min = ote2Values.length ? Math.min(...ote2Values) : 0
    const ote2Max = ote2Values.length ? Math.max(...ote2Values) : 0

    // Traffic-light colors cho 4 span cells (per-bank aggregate).
    const tlME1 = trafficLight(agg.perMarginExcess1)
    const tlME2 = trafficLight(agg.perMarginExcess2)
    const tlTy1 = trafficLight(agg.tyLeKyQuy)
    const tlTy2 = trafficLight(agg.tyLeKyQuy2)

    // Scenario banner: green/red theo price_change (mirror txtLbl* VBA).
    const scenarioBnr = scenarioBannerColor(priceChange)

    return (
      <TableBody>
        {bank.key === 'Vietinbank' ? (
          <TableRow className="margin-bank-section-labels">
            <TableCell colSpan={10} sx={{ bgcolor: '#fffefb', verticalAlign: 'middle' }} />
            <TableCell
              colSpan={5}
              sx={{
                '&&': {
                  ...propTitleSx,
                  display: 'table-cell',
                  height: 22,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  backgroundImage: 'none',
                  py: 0.35,
                },
              }}
            >
              Dự phòng giá biến động
            </TableCell>
            <TableCell
              colSpan={2}
              sx={{
                '&&': {
                  ...darkTitleSx,
                  display: 'table-cell',
                  height: 22,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                  backgroundImage: 'none',
                  py: 0.35,
                },
              }}
            >
              Stop-loss margin ratio
            </TableCell>
          </TableRow>
        ) : null}
        <TableRow>
          <TableCell component="th" className="bank-banner" colSpan={10} scope="colgroup">
            {bank.label}
          </TableCell>
          <TableCell
            component="th"
            className="scenario-banner"
            colSpan={5}
            scope="colgroup"
            sx={{ backgroundColor: scenarioBnr.bg, color: scenarioBnr.fg, backgroundImage: 'none' }}
          >
            {bank.label}
          </TableCell>
          <TableCell component="th" className="stoploss-banner" colSpan={2} scope="colgroup">
            {bank.label}
          </TableCell>
        </TableRow>
        <TableRow className="bank-subhead">
          <TableCell component="th" scope="col">
            Month
          </TableCell>
          <TableCell component="th" scope="col">
            Positions
          </TableCell>
          <TableCell component="th" scope="col">
            Avg Price
          </TableCell>
          <TableCell component="th" scope="col">
            Initial Margin
          </TableCell>
          <TableCell component="th" scope="col">
            OTE/UPL
          </TableCell>
          <TableCell component="th" scope="col">
            {oteUpl2Label}
          </TableCell>
          <TableCell component="th" scope="col">
            Balance
          </TableCell>
          <TableCell component="th" scope="col">
            Margin Excess
          </TableCell>
          <TableCell component="th" scope="col">
            % Margin Excess/NLV
          </TableCell>
          <TableCell component="th" scope="col">
            No. lots
          </TableCell>
          {/* col 11 sub-header placeholder — Market scenario banner cell render trong
              body row 1 với rowSpan để text dọc spans toàn data + total. */}
          <TableCell
            component="th"
            scope="col"
            sx={{
              backgroundColor: scenarioBnr.bg,
              color: scenarioBnr.fg,
              backgroundImage: 'none',
            }}
          />
          <TableCell component="th" scope="col">
            UPL
          </TableCell>
          <TableCell component="th" scope="col">
            Margin Excess
          </TableCell>
          <TableCell component="th" scope="col">
            % Margin Excess/NLV.
          </TableCell>
          <TableCell component="th" scope="col">
            No. lots
          </TableCell>
          <TableCell component="th" scope="col">
            Margin Ratio
          </TableCell>
          <TableCell component="th" scope="col">
            {tyLeKyQuy2Label}
          </TableCell>
        </TableRow>
        {displayRows.map((row, idx) => {
          const ote2 = row ? pickNumber(row, 'oteUpl2') : null
          const heatBg = heatMapBgColor(ote2, ote2Min, ote2Max)
          return (
            <TableRow key={`row-${bank.key}-${idx}`} className="month-row" sx={monthRowSx}>
              <TableCell>{row ? String(row.fullSymbol ?? row.month ?? '') : ''}</TableCell>
              <TableCell className="num" sx={marginOrangeNumSx}>
                {row ? displayPosition(pickNumber(row, 'openPosition')) : ''}
              </TableCell>
              <TableCell className="num" sx={marginAvgPriceNumSx}>
                {row ? displayMoney(pickNumber(row, 'avgPrice')) : ''}
              </TableCell>
              <TableCell className="num" sx={marginOrangeNumSx}>
                {row ? displayMoney(pickNumber(row, 'initialMargin')) : ''}
              </TableCell>
              <TableCell className="num" sx={marginOrangeNumSx}>
                {row ? displayMoney(pickNumber(row, 'oteUpl')) : ''}
              </TableCell>
              <TableCell
                className="num"
                sx={
                  heatBg
                    ? { backgroundColor: heatBg, color: MARGIN_DESIGN_FORE_ORANGE, fontWeight: 700 }
                    : marginOrangeNumSx
                }
              >
                {row ? displayMoney(ote2) : ''}
              </TableCell>
              {idx === 0 ? (
                <>
                  <TableCell className="num span-cell" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayMoney2(agg.balance)}
                  </TableCell>
                  <TableCell className="num span-cell" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayMoney2(agg.marginExcess1)}
                  </TableCell>
                  <TableCell
                    className="num span-cell"
                    rowSpan={aggRowSpan}
                    sx={
                      tlME1
                        ? { backgroundColor: tlME1.bg, color: tlME1.fg, fontWeight: 800 }
                        : { color: '#000000', fontWeight: 700 }
                    }
                  >
                    {displayPercent(agg.perMarginExcess1)}
                  </TableCell>
                  <TableCell className="num span-cell" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayLotsOrText(agg.noLots1)}
                  </TableCell>
                  {/* col 11 — Market scenario marker (vertical text, txtLblVTB Access).
                      Mã màu xanh/đỏ theo price_change scenario; rowSpan spans toàn body + total. */}
                  <TableCell
                    rowSpan={aggRowSpan}
                    sx={{
                      backgroundColor: scenarioBnr.bg,
                      color: scenarioBnr.fg,
                      padding: '2px 0 !important',
                      verticalAlign: 'middle',
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        writingMode: 'vertical-rl',
                        transform: 'rotate(180deg)',
                        fontWeight: 800,
                        letterSpacing: '0.02em',
                        fontSize: '0.56rem',
                        whiteSpace: 'nowrap',
                        mx: 'auto',
                      }}
                    >
                      {marketLabel}
                    </Box>
                  </TableCell>
                  <TableCell className="num span-cell-scenario" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayMoney2(agg.upl)}
                  </TableCell>
                  <TableCell className="num span-cell-scenario" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayMoney2(agg.marginExcess2)}
                  </TableCell>
                  <TableCell
                    className="num span-cell-scenario"
                    rowSpan={aggRowSpan}
                    sx={
                      tlME2
                        ? { backgroundColor: tlME2.bg, color: tlME2.fg, fontWeight: 800 }
                        : { color: '#000000', fontWeight: 700 }
                    }
                  >
                    {displayPercent(agg.perMarginExcess2)}
                  </TableCell>
                  <TableCell className="num span-cell-scenario" rowSpan={aggRowSpan} sx={marginOrangeNumSx}>
                    {displayLotsOrText(agg.noLots2)}
                  </TableCell>
                  <TableCell
                    className="num span-cell-stoploss"
                    rowSpan={aggRowSpan}
                    sx={
                      tlTy1
                        ? { backgroundColor: tlTy1.bg, color: tlTy1.fg, fontWeight: 800 }
                        : { color: '#000000', fontWeight: 700 }
                    }
                  >
                    {displayPercent(agg.tyLeKyQuy)}
                  </TableCell>
                  <TableCell
                    className="num span-cell-stoploss"
                    rowSpan={aggRowSpan}
                    sx={
                      tlTy2
                        ? { backgroundColor: tlTy2.bg, color: tlTy2.fg, fontWeight: 800 }
                        : { color: '#000000', fontWeight: 700 }
                    }
                  >
                    {displayPercent(agg.tyLeKyQuy2)}
                  </TableCell>
                </>
              ) : null}
            </TableRow>
          )
        })}
        <TableRow className="total">
          <TableCell>Total</TableCell>
          <TableCell className="num" sx={marginOrangeNumSx}>
            {displayPosition(totals.positions)}
          </TableCell>
          <TableCell />
          <TableCell className="num" sx={marginOrangeNumSx}>
            {displayMoney(totals.initialMargin)}
          </TableCell>
          <TableCell className="num" sx={marginOrangeNumSx}>
            {displayMoney(totals.oteUpl)}
          </TableCell>
          <TableCell className="num" sx={marginOrangeNumSx}>
            {displayMoney(totals.oteUpl2)}
          </TableCell>
          {/* cols 7–16 are merged from the first month row via rowSpan */}
        </TableRow>
      </TableBody>
    )
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={commandBarSx}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 'auto', ml: 0.5 }}>
          {/* <Typography variant="caption" sx={{ fontWeight: 700, color: '#000000' }}>
            Margin Report
          </Typography> */}
          {fetching && !loading ? <CircularProgress size={12} sx={{ color: 'primary.main' }} /> : null}
        </Stack>
        <Button
          size="small"
          variant="outlined"
          sx={commandBtnSx}
          disabled={!canEdit}
          onClick={() => setChangeOpen(true)}
        >
          Change Input Value
        </Button>
      </Box>

      <Box sx={reportShellSx}>
        {hasError ? (
          <Alert severity="error" sx={{ mb: 1, borderRadius: 2, fontSize: '0.72rem', py: 0.5 }}>
            Could not load part of the Margin Report data. Please try again later.
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={24} sx={{ color: 'primary.main' }} />
          </Box>
        ) : (
          <Box sx={reportPageSx}>
            {/* ── Top header row 1: Hedging Config (cols 1-9) + Ngày (col 10) + BẢNG QUY ĐỔI (cols 11-17)
                Layout 17-col: cột 10 ("Ngày") nằm trên đúng cột "No. lots" của bank table (Access R7 C9). */}
            <Box sx={marginReportGridSx}>
              <Box sx={{ gridColumn: '1 / 10', display: 'flex', flexDirection: 'column', pr: 0.75 }}>
                <Box sx={topBannerSectionTitleSx}>Hedging Configuration</Box>
                <Box
                  sx={{
                    border: '1px solid rgba(11, 54, 51, 0.12)',
                    borderTop: 0,
                    p: 0.75,
                    display: 'grid',
                    // 2 cặp label-value mỗi hàng → 4 cột (auto label / 1fr value) × 2
                    gridTemplateColumns: 'auto minmax(0, 1fr) auto minmax(0, 1fr)',
                    columnGap: 1.5,
                    rowGap: 0.5,
                    bgcolor: '#fffefb',
                  }}
                >
                  {renderConfigField('Lots to hedge', lotHedge ? `${displayNumber(lotHedge)} lots` : '—', true)}
                  {renderConfigField(
                    `Total P&L when Market ${priceChange > 0 ? '+' : ''}${priceChange || 0}$`,
                    displayMoney2(grandTotalMarginExcess2),
                    true,
                  )}
                  {renderConfigField('Required margin excess (USD)', lotsNeed ? displayMoney2(lotsNeed) : '—', true)}
                  {renderConfigField('In VND', lotsNeedVnd ? displayVnd(lotsNeedVnd) : '—', true)}
                  {renderConfigField(
                    'Price change',
                    priceChange ? `${priceChange > 0 ? '+' : ''}${priceChange} $` : '—',
                    true,
                  )}
                  {renderConfigField('Margin/lot - Robusta', kyquyRobusta ? displayMoney2(kyquyRobusta) : '—', true)}
                  {renderConfigField('Margin/lot - Arabica', kyquyArabica ? displayMoney2(kyquyArabica) : '—', true)}
                </Box>
              </Box>

              {/* col 10 — "Ngày" cell aligned ngay trên "No. lots" của bank table (Access R7 C9 + R8 C9=Date()). */}
              <Box sx={{ gridColumn: '10 / 11', display: 'flex', flexDirection: 'column', px: 0.25 }}>
                <Box sx={topBannerSectionTitleSx}>Date</Box>
                <Box
                  sx={{
                    border: '1px solid rgba(11, 54, 51, 0.12)',
                    borderTop: 0,
                    p: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    bgcolor: '#fffefb',
                    flex: 1,
                  }}
                >
                  {todayStr()}
                </Box>
              </Box>

              <Box sx={{ gridColumn: '11 / 18', display: 'flex', flexDirection: 'column', pl: 0.5 }}>
                <Box sx={topBannerPropTitleSx}>Conversion Table</Box>
                <Box
                  sx={{
                    border: '1px solid rgba(11, 54, 51, 0.12)',
                    borderTop: 0,
                    p: 0.75,
                    display: 'grid',
                    // 1 cặp label-value mỗi hàng → 2 cột (auto / 1fr)
                    gridTemplateColumns: 'auto minmax(0, 1fr)',
                    columnGap: 1,
                    rowGap: 0.5,
                    bgcolor: '#fffefb',
                  }}
                >
                  {renderConfigField(
                    `Convert to VND (${changeUsd ? displayNumber(changeUsd) : '—'} USD)`,
                    usdToVnd ? displayVnd(usdToVnd) : '—',
                    true,
                  )}
                  {renderConfigField(
                    `Convert to USD (${changeVnd ? displayNumber(changeVnd) : '—'} VND)`,
                    vndToUsd ? displayMoney2(vndToUsd) : '—',
                    true,
                  )}
                  {renderConfigField('USD/VND Rate', usdVndRate ? displayNumber(usdVndRate) : '—', true)}
                </Box>
              </Box>
            </Box>

            {/* ── 3 bank + Grand Total: một Table để cột thẳng hàng một khối ── */}
            <Box sx={{ mt: 1.25, width: 'max-content', minWidth: '100%' }}>
              <Table size="small" sx={tableSx}>
                {BANKS.map((bank, bankIdx) => (
                  <Fragment key={bank.key}>
                    {renderBankTableBody(bank)}
                    {bankIdx < BANKS.length - 1 ? (
                      <TableBody className="bank-section-spacer">
                        <TableRow>
                          <TableCell colSpan={17} />
                        </TableRow>
                      </TableBody>
                    ) : null}
                  </Fragment>
                ))}
                <TableBody>
                  <TableRow className="grand-total">
                    <TableCell>Grand Total</TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayPosition(grandTotalPositions)}
                    </TableCell>
                    <TableCell />
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney(grandTotalInitialMargin)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney(grandTotalOteUpl)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney(grandTotalOteUpl2)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney2(grandTotalBalance)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney2(grandTotalMarginExcess1)}
                    </TableCell>
                    <TableCell />
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {grandTotalNoLots1 != null ? displayLots(grandTotalNoLots1) : ''}
                    </TableCell>
                    {/* col 11: empty in grand total (Market marker chỉ áp dụng per-bank) */}
                    <TableCell />
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney2(grandTotalUpl)}
                    </TableCell>
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {displayMoney2(grandTotalMarginExcess2)}
                    </TableCell>
                    <TableCell />
                    <TableCell className="num" sx={marginOrangeNumSx}>
                      {grandTotalNoLots2 != null ? displayLots(grandTotalNoLots2) : ''}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </Box>

      <ChangeInputValueDialog
        open={changeOpen}
        initialValues={cfg}
        saving={putState.isLoading}
        onClose={() => setChangeOpen(false)}
        onSave={(v) => void handleSave(v)}
      />
    </Box>
  )
}
